/* eslint-disable max-statements */
import { Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { cloneDeep } from 'lodash';
import { useNavigate, useParams } from 'react-router-dom';

import { TSDHouseConsignment } from '@e-origin/shared';

import { copyIcon, deleteIcon, downloadIcon, layerMultipleIcon, uploadIcon, viewIcon } from '@assets';
import { TableActions, TableColumnProps, UploadFileModal } from '@components';
import { useTsdDownloadHouseConsignmentItems, useTsdUploadHouseConsignmentItems } from '@hooks/tsd';

export const useHousesListColumns = ({ tsdName, consignmentForm, openHouseConsignment, queryParams, disabled, selectedRow }) => {
  const { id } = useParams<{ id: string }>() as any;
  const uploadHouseConsignmentItemsMutation = useTsdUploadHouseConsignmentItems();
  const downloadHouseConsignmentItemsMutation = useTsdDownloadHouseConsignmentItems();

  const columns: TableColumnProps<TSDHouseConsignment>[] = [
    {
      name: 'Consignor Id',
      cell: (row) => row.consignor?.identificationNumber,
      sortable: false,
    },
    {
      name: 'Consignor name',
      cell: (row) => row.consignor?.name,
      sortable: false,
    },
    {
      name: 'Consignee Id',
      cell: (row) => row.consignee?.identificationNumber,
      sortable: false,
    },
    {
      name: 'Consignee name',
      cell: (row) => row.consignee?.name,
      sortable: false,
    },
    {
      name: 'Goods item',
      cell: (row, rowIdx) => <span data-pw={`cell-house-consignment-goods-item-${rowIdx}`}>{row.consignmentItems?.length || 0}</span>,
      sortable: false,
    },
    {
      name: 'Reference Number UCR',
      cell: (row) => row.referenceNumberUCR,
      sortable: false,
    },
    {
      name: 'Gross mass',
      cell: (row, rowIdx) => <span data-pw={`cell-house-consignment-gross-mass-${rowIdx}`}>{row.totalGrossMass || 'N/A'}</span>,
      sortable: false,
    },
  ];

  const navigate = useNavigate();

  const handleHouseDuplicate = (houseConsignmentIdx?: number) => {
    const house = houseConsignmentIdx !== null ? consignmentForm.values.houseConsignment[houseConsignmentIdx] : consignmentForm.values.master;
    consignmentForm.setFieldValue('houseConsignment', [
      ...consignmentForm.values.houseConsignment,
      {
        ...cloneDeep(house),
        consignmentItems: [],
        transportEquipment: [],
        batches: [],
        receptacle: [],
        transportDocuments: [],
        totalGrossMass: 0,
        totalNumberOfPackages: 0,
      },
    ]);
    selectedRow.current = null;
  };

  const handleHouseDelete = (houseConsignmentIdx: number) => {
    consignmentForm.setFieldValue(
      'houseConsignment',
      consignmentForm.values.houseConsignment.filter((_, idx) => idx !== houseConsignmentIdx),
    );
    selectedRow.current = null;
  };

  const openDeleteHouseConsignment = (houseConsignmentIdx: number) => {
    modals.openConfirmModal({
      title: 'Confirm house consignment delete',
      children: <Text>Are you sure you want to delete this house consignment?</Text>,
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => handleHouseDelete(houseConsignmentIdx),
    });
  };

  const handleHouseDeleteMaster = () => {
    consignmentForm.setFieldValue('master', null);
    selectedRow.current = null;
  };

  const openDeleteMasterConsignment = () => {
    modals.openConfirmModal({
      title: 'Confirm master consignment delete',
      children: <Text>Are you sure you want to delete the master consignment?</Text>,
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => handleHouseDeleteMaster(),
    });
  };

  const actionList = (house?: TSDHouseConsignment, houseIndex?: number, isMaster?: boolean) => {
    return [
      {
        icon: viewIcon,
        text: 'Edit',
        onClick: () => {
          openHouseConsignment(houseIndex, isMaster);
        },
        disableAfterClick: false,
      },
      {
        icon: layerMultipleIcon,
        text: 'View Goods Items',
        onClick: () => {
          queryParams.current.set('context', 'GOODS');
          queryParams.current.set('houseRef', isMaster ? 'master' : consignmentForm?.values?.houseConsignment?.indexOf(house).toString());
          navigate({ search: queryParams.current.toString() });
        },
      },
      {
        if: !disabled,
        icon: copyIcon,
        text: 'Duplicate house',
        onClick: () => handleHouseDuplicate(isMaster ? null : houseIndex),
        preventDefaultClick: true,
        disableAfterClick: false,
      },
      {
        if: !disabled,
        icon: deleteIcon,
        text: 'Delete',
        onClick: () => {
          isMaster ? openDeleteMasterConsignment() : openDeleteHouseConsignment(houseIndex);
        },
        disableAfterClick: false,
      },
    ];
  };

  const handleFileSubmit = async (file: File, houseIndexForUpload: number | 'master') => {
    const updatedHouseConsignment = await uploadHouseConsignmentItemsMutation.mutateAsync({
      tsdId: id,
      houseConsignmentIndex: houseIndexForUpload,
      file,
    });
    if (updatedHouseConsignment) {
      if (houseIndexForUpload === 'master') {
        consignmentForm.setFieldValue(`master.consignmentItems`, updatedHouseConsignment.consignmentItems);
        consignmentForm.setFieldValue(`master.batches`, []);
      } else {
        consignmentForm.setFieldValue(`houseConsignment.${houseIndexForUpload}.consignmentItems`, updatedHouseConsignment.consignmentItems);
        consignmentForm.setFieldValue(`houseConsignment.${houseIndexForUpload}.batches`, []);
      }
    }
  };

  const openUploadItems = (houseIndex: number | 'master') => {
    modals.open({
      title: 'Upload items',
      children: <UploadFileModal onHide={modals.closeAll} onFileSubmit={(file) => handleFileSubmit(file, houseIndex)} />,
    });
  };

  const secondaryActions = (houseIndex: number, isMaster: boolean) => {
    return [
      {
        icon: downloadIcon,
        text: 'Download items',
        onClick: () => {
          downloadHouseConsignmentItemsMutation.mutate({
            tsdId: id,
            tsdName,
            houseConsignmentIndex: isMaster ? 'master' : houseIndex,
          });
        },
        preventDefaultClick: true,
      },
      {
        if: !disabled,
        icon: uploadIcon,
        text: 'Upload items',
        onClick: () => {
          openUploadItems(isMaster ? 'master' : houseIndex);
        },
        preventDefaultClick: true,
      },
    ];
  };

  const actionsColumn = {
    name: 'Actions',
    sticky: 'right',
    cell: (row, rowIndex) => (
      <TableActions
        actions={actionList(row, rowIndex, false)}
        secondaryActions={secondaryActions(rowIndex, false)}
        onClick={() => {
          selectedRow.current = {
            houseConsignmentIdx: rowIndex,
            houseConsigment: row,
          };
        }}
        disableAfterClick={true}
        data-pw={rowIndex}
      />
    ),
  };

  const masterActionsColumn = {
    name: 'Actions',
    sticky: 'right',
    cell: (row, rowIndex) => (
      <TableActions
        actions={actionList(row, rowIndex, true)}
        secondaryActions={secondaryActions(rowIndex, true)}
        onClick={() => {
          selectedRow.current = {
            houseConsignmentIdx: rowIndex,
            houseConsigment: row,
          };
        }}
        disableAfterClick={true}
        data-pw={rowIndex}
      />
    ),
  };

  return { columns, actionsColumn, masterActionsColumn, secondaryActions };
};
